import { useDimensions } from '@/hooks/use-dimensions.hook';
import { useEffect, useRef, useState } from 'react';

type TailoringLayoutReturn = {
  screen: {
    height: number;
    width: number;
  };
  container: {
    height: number;
  };
  left: {
    width: number;
    fixed: {
      ref: React.MutableRefObject<HTMLDivElement | null>;
      height: number;
    };
    scroll: {
      height: number;
    };
  };
  right: {
    width: number;
    fixed: {
      ref: React.MutableRefObject<HTMLDivElement | null>;
      height: number;
    };
    scroll: {
      height: number;
    };
  };
};

// needed for container width calulations
const LEFT_CONTAINER_PERCENTAGE = 0.6;
const RIGHT_CONTAINER_PERCENTAGE = 0.4;
const PADDING_BEETWEEN_CONTAINERS = 24;
const SIDE_MARGIN = 72;

// needed for container height calculations
const HEADER_HEIGHT = 64;
const TOPBAR_HEIGHT = 80;
const FOOTER_HEIGHT = 40;

// needed for scroll area height calculations
const TOP_CONTAINER_PADDING = 24;
const BOTTOM_CONTAINER_PADDING = 16;

export const useTailoringLayout = (): TailoringLayoutReturn => {
  const { height: screenHeight, width: screenWidth } = useDimensions();

  const leftWidth = screenWidth * LEFT_CONTAINER_PERCENTAGE - PADDING_BEETWEEN_CONTAINERS / 2 - SIDE_MARGIN;
  const rightWidth = screenWidth * RIGHT_CONTAINER_PERCENTAGE - PADDING_BEETWEEN_CONTAINERS / 2 - SIDE_MARGIN;

  const leftRef = useRef<HTMLDivElement | null>(null);
  const rightRef = useRef<HTMLDivElement | null>(null);

  const containerHeight = screenHeight - HEADER_HEIGHT - TOPBAR_HEIGHT - FOOTER_HEIGHT;

  const [leftFixedElementHeight, setLeftFixedElementHeight] = useState(0);
  const [rightFixedElementHeight, setRightFixedElementHeight] = useState(0);

  useEffect(() => {
    if (!leftRef.current) return;
    const resizeObserver = new ResizeObserver(() => {
      setLeftFixedElementHeight(leftRef.current?.clientHeight || 0);
    });
    resizeObserver.observe(leftRef.current);
    return () => resizeObserver.disconnect();
  }, [leftRef.current]);

  useEffect(() => {
    if (!rightRef.current) return;
    const resizeObserver = new ResizeObserver(() => {
      setRightFixedElementHeight(rightRef.current?.clientHeight || 0);
    });
    resizeObserver.observe(rightRef.current);
    return () => resizeObserver.disconnect();
  }, [rightRef.current]);

  const leftScrollAreaHeight =
    containerHeight - leftFixedElementHeight - TOP_CONTAINER_PADDING - BOTTOM_CONTAINER_PADDING;
  const rightScrollAreaHeight =
    containerHeight - rightFixedElementHeight - TOP_CONTAINER_PADDING - BOTTOM_CONTAINER_PADDING;

  return {
    screen: {
      height: screenHeight,
      width: screenWidth,
    },
    container: {
      height: containerHeight,
    },
    left: {
      width: leftWidth,
      fixed: {
        ref: leftRef,
        height: leftFixedElementHeight,
      },
      scroll: {
        height: leftScrollAreaHeight,
      },
    },
    right: {
      width: rightWidth,
      fixed: {
        ref: rightRef,
        height: rightFixedElementHeight,
      },
      scroll: {
        height: rightScrollAreaHeight,
      },
    },
  };
};
